import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from "axios";

import { BASE_URL, STORE_KEY } from "@/configs";
import { API_URLS, ROUTES } from "@/constants";
import { dispatch, logout, store } from "@/store";

import { recordListResponse } from "./export/listRecorder";
import { getDeviceProof } from "./fingerprint";

// redux-persist stores the whitelisted auth slice under this localStorage key
// (default `persist:` prefix + the configured store key).
const PERSIST_KEY = `persist:${STORE_KEY}`;

const axiosInstance: AxiosInstance = axios.create({
  baseURL: `${BASE_URL}/api-admin/`,
  timeout: 10000,
  // A cross-origin response may only set the session-restore cookie on a credentialed request.
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// Each fingerprint is billed. Not AUTH.ADMINLOGIN: that row is the target's, and this browser is not theirs.
const SIGN_IN_URLS: string[] = [
  API_URLS.AUTH.LOGIN,
  API_URLS.AUTH.VERIFY_2FA,
  API_URLS.AUTH.SETUP_2FA_VERIFY,
];

axiosInstance.interceptors.request.use(
  async (config) => {
    const token = store.getState().auth.token;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (SIGN_IN_URLS.includes(config.url ?? "")) {
      const proof = await getDeviceProof();
      if (proof) config.headers["x-device-proof"] = proof;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

axiosInstance.interceptors.response.use(
  (response) => {
    // Lets a grid's Export fetch every page of the list it shows.
    recordListResponse(response.config, response.data);

    return response;
  },
  (error) => {
    const status = error.response?.status;
    // Only a 401 ends the session. A timeout or dropped connection must not throw away open forms;
    // callers show it as an error instead.
    const isDeadSession = status === 401;

    // 401 = no/expired session (bad JWT, missing token, or sessionCheck
    // tossed the row - e.g. the idle TTL reaped it server-side). The local
    // token is useless, so clear the auth state, purge the persisted token
    // from localStorage so a reload can't rehydrate it, and send the user
    // straight to the login page.
    if (isDeadSession && typeof window !== "undefined") {
      console.error("Session expired. Clearing token.");
      dispatch(logout());
      // Synchronous removal - `dispatch(logout())` only flushes to localStorage
      // on redux-persist's async tick, which can lose the race against the
      // redirect below and leave the stale token behind.
      localStorage.removeItem(PERSIST_KEY);
      // `replace` (not href/reload) so the dead-session page doesn't linger in
      // history, and we skip the redirect if we're already on /login.
      if (window.location.pathname !== ROUTES.LOGIN) {
        window.location.replace(ROUTES.LOGIN);
      }
    }

    // 403 = per-call permission failure. Let it propagate; callers display
    // the error message via handleError. The interceptor used to toast here
    // as a safety net, but every caller already calls handleError so doing
    // both produced duplicate toasts.

    console.error("API Error:", error.response?.data?.message || error.message);

    return Promise.reject(error);
  },
);

interface ApiRequest extends AxiosRequestConfig {
  errorMessage: string;
}

// An Error carrying a `silent` flag so callers (via handleError) can skip the
// toast. Set for 401s, which the response interceptor already handles globally
// by logging out and redirecting to /login.
export interface SilentableError extends Error {
  silent?: boolean;
  // The server's answer; absent when none came (timeout, dropped connection).
  status?: number;
}

const apiRequest = async <T>(config: ApiRequest): Promise<T> => {
  try {
    const response = await axiosInstance(config);

    return response.data;
  } catch (error) {
    const status = error instanceof AxiosError ? error.response?.status : undefined;
    const message =
      error instanceof AxiosError
        ? error.response?.data?.message
        : error instanceof Error
          ? error.message
          : "An unknown error occurred";
    const wrapped: SilentableError = new Error(message || config.errorMessage);
    wrapped.status = status;
    // The interceptor above already cleared auth and redirected to login on a 401 - suppress the
    // per-call toast so a page that fired N requests doesn't stack up N "session expired" toasts.
    if (status === 401) {
      wrapped.silent = true;
    }
    throw wrapped;
  }
};

// For callers that replay a recorded request (grid export); errors surface as from apiRequest.
export const requestRaw = (config: AxiosRequestConfig): Promise<unknown> =>
  apiRequest({ ...config, errorMessage: "Failed to load the data. Please try again." });

export default apiRequest;
